import { useMemo, useState } from "react";
import { toast } from "sonner";
import { submitExperience } from "@/lib/api";

const DISPLAY_OPTIONS = [
  { value: "first_last_initial", label: "First name + last initial", example: "Brandon E." },
  { value: "first_only", label: "First name only", example: "Brandon" },
  { value: "initials", label: "Initials only", example: "B.E." },
  { value: "anonymous", label: "Anonymous", example: "TENET Client" },
];

function computeDisplayName(first, last, option) {
  first = (first || "").trim();
  last = (last || "").trim();
  if (option === "anonymous") return "TENET Client";
  if (option === "first_only") return first || "TENET Client";
  if (option === "initials") {
    const fi = first.slice(0, 1).toUpperCase();
    const li = last.slice(0, 1).toUpperCase();
    return li ? `${fi}.${li}.` : fi ? `${fi}.` : "TENET Client";
  }
  if (last) return `${first} ${last.slice(0, 1).toUpperCase()}.`;
  return first || "TENET Client";
}

async function compressImage(file) {
  const dataUrl = await new Promise((res) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.readAsDataURL(file);
  });
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = dataUrl;
  });
  const max = 600;
  let { width, height } = img;
  if (width > height && width > max) {
    height = Math.round((height * max) / width);
    width = max;
  } else if (height > max) {
    width = Math.round((width * max) / height);
    height = max;
  }
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  canvas.getContext("2d").drawImage(img, 0, 0, width, height);
  return canvas.toDataURL("image/jpeg", 0.8);
}

const fieldClass =
  "w-full rounded-xl border border-stone-300 bg-white px-4 py-3 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-[#1F3D2E]/40";

export default function ExperienceForm() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [displayOption, setDisplayOption] = useState("first_last_initial");
  const [experience, setExperience] = useState("");
  const [advice, setAdvice] = useState("");
  const [photo, setPhoto] = useState(null);
  const [publishConsent, setPublishConsent] = useState(false);
  const [editConsent, setEditConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const preview = useMemo(
    () => computeDisplayName(firstName, lastName, displayOption),
    // computeDisplayName is a stable module-level function
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [firstName, lastName, displayOption]
  );

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setPhoto(await compressImage(file));
    } catch {
      toast.error("We couldn't read that image. Please try another file.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!firstName.trim()) return toast.error("Please enter your first name.");
    if (!experience.trim()) return toast.error("Please tell us about your experience.");
    if (!publishConsent) return toast.error("Please give permission to publish your experience.");
    if (!editConsent) return toast.error("Please acknowledge that minor edits may be made.");
    setLoading(true);
    try {
      const res = await submitExperience({
        first_name: firstName,
        last_name: lastName,
        display_option: displayOption,
        experience,
        advice,
        photo,
        publish_consent: publishConsent,
        edit_consent: editConsent,
      });
      toast.success(res?.message || "Thank you for sharing your experience.");
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-8" data-testid="experience-form-success">
        <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-[#EAF2EC]">
          <i className="fa-solid fa-heart text-[#4A7C59] text-xl" />
        </div>
        <h3 className="font-playfair text-2xl text-stone-900 mt-4">Thank you for sharing your experience</h3>
        <p className="text-stone-600 mt-3 max-w-md mx-auto leading-relaxed">
          Your story may help someone who is wondering whether change is possible — or whether they
          are ready to take the next step. Thank you for allowing us to be part of your journey.
        </p>
        <p className="text-xs text-stone-400 mt-4">
          Experiences are reviewed before appearing on the site.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="experience-form">
      <div>
        <h3 className="font-playfair text-2xl text-stone-900">Tell us about your experience</h3>
        <p className="text-sm text-stone-600 mt-2 leading-relaxed">
          You are welcome to share as much or as little as you are comfortable sharing. Please do not
          include highly sensitive personal information that you would not want published.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-stone-700 mb-1.5">
            First name<span className="text-[#1F3D2E]"> *</span>
          </label>
          <input
            data-testid="experience-first-name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className={fieldClass}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-stone-700 mb-1.5">Last name</label>
          <input
            data-testid="experience-last-name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className={fieldClass}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-stone-700 mb-2">
          How would you like your name displayed?
        </label>
        <div className="grid sm:grid-cols-2 gap-2.5" data-testid="experience-display-options">
          {DISPLAY_OPTIONS.map((o) => (
            <label
              key={o.value}
              className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 text-sm transition-colors ${
                displayOption === o.value
                  ? "border-[#1F3D2E] bg-[#EAF2EC]/60"
                  : "border-stone-300 hover:bg-stone-50"
              }`}
            >
              <input
                type="radio"
                name="display_option"
                data-testid={`experience-display-${o.value}`}
                checked={displayOption === o.value}
                onChange={() => setDisplayOption(o.value)}
                className="accent-[#1F3D2E]"
              />
              <span className="text-stone-700">{o.label}</span>
            </label>
          ))}
        </div>
        <p className="mt-2 text-xs text-stone-500">
          Your name will appear as{" "}
          <span className="font-semibold text-[#1F3D2E]" data-testid="experience-name-preview">
            {preview}
          </span>
          . Anonymity is completely welcome.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-stone-700 mb-1.5">
          What was your experience with TENET like?<span className="text-[#1F3D2E]"> *</span>
        </label>
        <textarea
          data-testid="experience-text"
          value={experience}
          onChange={(e) => setExperience(e.target.value)}
          rows={6}
          className={fieldClass}
        />
        <p className="mt-2 text-xs text-stone-500 leading-relaxed">
          You might consider sharing: how you felt before starting, whether you had any concerns or
          skepticism, what you found helpful, what changed in the way you viewed yourself or your
          situation, and what you would tell someone considering TENET.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-stone-700 mb-1.5">
          What would you tell someone who is unsure about starting?
        </label>
        <textarea
          data-testid="experience-advice"
          value={advice}
          onChange={(e) => setAdvice(e.target.value)}
          rows={3}
          className={fieldClass}
        />
      </div>

      <div className="space-y-3">
        <label className="flex items-start gap-3 text-sm text-stone-600">
          <input
            type="checkbox"
            data-testid="experience-publish-consent"
            checked={publishConsent}
            onChange={(e) => setPublishConsent(e.target.checked)}
            className="mt-1 h-4 w-4 accent-[#1F3D2E]"
          />
          <span>
            I give TENET permission to publish the experience I submitted on its website, marketing
            materials, social media, and educational materials using the name-display option I
            selected above.
          </span>
        </label>
        <label className="flex items-start gap-3 text-sm text-stone-600">
          <input
            type="checkbox"
            data-testid="experience-edit-consent"
            checked={editConsent}
            onChange={(e) => setEditConsent(e.target.checked)}
            className="mt-1 h-4 w-4 accent-[#1F3D2E]"
          />
          <span>
            I understand that TENET may make minor edits for spelling, grammar, clarity, or length,
            provided the meaning of my comments is not materially changed.
          </span>
        </label>
      </div>

      <div>
        <label className="block text-sm font-medium text-stone-700 mb-1.5">
          Would you like to provide a photo?
        </label>
        <div className="flex items-center gap-4">
          {photo && (
            <img src={photo} alt="Your upload preview" className="h-14 w-14 rounded-full object-cover ring-1 ring-stone-200" />
          )}
          <label className="cursor-pointer rounded-full border border-stone-300 px-4 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50">
            <input type="file" accept="image/*" onChange={onPhoto} className="hidden" data-testid="experience-photo" />
            {photo ? "Change photo" : "Upload a photo"}
          </label>
          {photo && (
            <button type="button" onClick={() => setPhoto(null)} className="text-sm text-stone-400 hover:text-stone-600">
              Remove
            </button>
          )}
        </div>
        <p className="mt-2 text-xs text-stone-500">Providing a photo is completely optional.</p>
      </div>

      <button
        type="submit"
        disabled={loading}
        data-testid="experience-submit"
        className="btn-terracotta w-full rounded-full px-6 py-3.5 text-sm font-semibold disabled:opacity-60"
      >
        {loading ? "Sending\u2026" : "Submit my experience"}
      </button>
    </form>
  );
}
